import React, { useState, useEffect, useMemo } from 'react';
import { DropdownBase, DropdownItem } from 'components/forms/Dropdown';
import { Question, PossibleAnswer } from '../../types/interfaces';
import { isHierarchicalAnswer } from '../../types/possibleAnswer';

interface HierarchicalQuestionComponentProps {
  question: Question; // La question racine qui contient childQuestions
  currentAnswerKey: string | number;
  onAnswer: (key: string, answer: PossibleAnswer[]) => void;
  maxDepth?: number; // Limite de profondeur pour éviter les boucles infinies
}

export const GenericHierarchicalQuestion: React.FC<HierarchicalQuestionComponentProps> = ({
  question,
  currentAnswerKey,
  onAnswer,
  maxDepth = 5
}) => {
  // État pour gérer tous les niveaux de sélection
  const [selections, setSelections] = useState<Record<number, DropdownItem | null>>({});

  // Fonction récursive pour construire la chaîne de questions
  const buildQuestionChain = useMemo(() => {
    const chain: Array<{
      question: Question;
      level: number;
      parentValue?: any;
    }> = [];

    // Fonction récursive pour parcourir la hiérarchie
    const traverseQuestions = (
      currentQuestion: Question,
      level: number,
      parentValue?: any
    ) => {
      if (level >= maxDepth) return; // Protection contre les boucles infinies

      chain.push({
        question: currentQuestion,
        level,
        parentValue
      });

      if (currentAnswerKey && currentQuestion.childQuestions) {
        const childQuestion = currentQuestion.childQuestions.find(child =>
          child.dependsOn &&
          child.dependsOn.questionKey === currentQuestion.key &&
          child.dependsOn.values.includes(currentAnswerKey)
        );

        if (childQuestion) {
          traverseQuestions(childQuestion, level + 1, currentAnswerKey);
        }
      }
    };

    traverseQuestions(question, 0);
    return chain;
  }, [question, currentAnswerKey, maxDepth]);

  // Conversion des réponses en items de dropdown pour chaque niveau
  const getDropdownItems = (questionAtLevel: Question): DropdownItem[] => {
    return questionAtLevel.possibleAnswers
      .filter(answer => isHierarchicalAnswer(answer) || typeof answer === 'object')
      .map(answer => {
        const anyAnswer = answer as any;
        return {
          key: anyAnswer.key,
          label: anyAnswer.label
        };
      });
  };

  // Synchronisation avec les réponses actuelles
  useEffect(() => {
    const newSelections: Record<number, DropdownItem | null> = {};

    buildQuestionChain.forEach(({ question: q, level }) => {
      if (currentAnswerKey) {
        const items = getDropdownItems(q);
        const selectedItem = items.find(item => item.key === currentAnswerKey);
        newSelections[level] = selectedItem || null;
      } else {
        newSelections[level] = null;
      }
    });

    setSelections(newSelections);
  }, [buildQuestionChain, currentAnswerKey]);

  // Gestionnaire de sélection générique
  const handleSelection = (level: number, selectedItem: DropdownItem) => {
    const questionAtLevel = buildQuestionChain[level];
    if (!questionAtLevel) return;

    // Mettre à jour la sélection pour ce niveau
    const newSelections = { ...selections };
    newSelections[level] = selectedItem;

    // Effacer toutes les sélections des niveaux inférieurs
    for (let i = level + 1; i < maxDepth; i++) {
      newSelections[i] = null;
    }

    setSelections(newSelections);

    // Trouver la réponse complète
    const fullAnswer = questionAtLevel.question.possibleAnswers.find(answer => {
      const anyAnswer = answer as any;
      return anyAnswer.key === selectedItem.key;
    });

    if (fullAnswer) {
      onAnswer(questionAtLevel.question.key, [fullAnswer]);
    }

    // Effacer les réponses des niveaux inférieurs
    buildQuestionChain.slice(level + 1).forEach(({ question: childQ }) => {
      if (currentAnswerKey) {
        onAnswer(childQ.key, []);
      }
    });
  };

  // Fonction pour déterminer si un niveau doit être affiché
  const shouldShowLevel = (level: number): boolean => {
    if (level === 0) return true; // Le premier niveau est toujours visible

    // Les niveaux suivants ne sont visibles que si le niveau précédent a une sélection
    return selections[level - 1] !== null && selections[level - 1] !== undefined;
  };

  return (
    <div className="w-full flex flex-col gap-6 items-center justify-center">
      {/* Rendu de tous les niveaux de la hiérarchie */}
      {buildQuestionChain.map(({ question: q, level }) => {
        if (!shouldShowLevel(level)) return null;

        const items = getDropdownItems(q);
        const selectedItem = selections[level] || null;

        return (
          <div
            key={`${q.key}-${level}`}
            className="flex flex-col gap-2 justify-center items-center w-full max-w-md"
          >
            <p className="text-xl text-center text-zinc-800 font-medium mb-4">
              {q.label}
            </p>
            <DropdownBase
              items={items}
              selectedItem={selectedItem}
              placeholder={`Sélectionnez une option ${level > 0 ? `(niveau ${level + 1})` : ''}...`}
              onSelect={(item) => handleSelection(level, item)}
              className="w-full"
            />
          </div>
        );
      })}
    </div>
  );
};

// Hook pour vérifier si une question est hiérarchique (inchangé)
export const useIsHierarchicalQuestion = (question: Question) => {
  return useMemo(() => {
    const hasHierarchicalAnswers = question.possibleAnswers.some(answer =>
      isHierarchicalAnswer(answer) && (answer as any).hasChildren
    );
    const hasChildQuestions = question.childQuestions && question.childQuestions.length > 0;

    return hasHierarchicalAnswers && hasChildQuestions;
  }, [question]);
};

// Hook pour analyser la profondeur maximale d'une hiérarchie
export const useHierarchyDepth = (question: Question): number => {
  return useMemo(() => {
    const calculateDepth = (q: Question, currentDepth = 0): number => {
      if (!q.childQuestions || q.childQuestions.length === 0) {
        return currentDepth;
      }

      let maxChildDepth = currentDepth;
      q.childQuestions.forEach(child => {
        const childDepth = calculateDepth(child, currentDepth + 1);
        maxChildDepth = Math.max(maxChildDepth, childDepth);
      });

      return maxChildDepth;
    };

    return calculateDepth(question) + 1; // +1 pour inclure la question racine
  }, [question]);
};
